// @flow

import * as React from 'react';
import { type DeepState, createDeepState } from '../../controls/deep-state';
import { useRouterEvents } from '../../controls/router';
import { isEqual } from '../../utils/isEqual';
import { useDebounce } from '../../hooks/debounce';
import {
  requestAnimationTimeout,
  cancelAnimationTimeout,
} from '../../utils/animation-timeout.js';
import { useViewportType } from '../../hooks/viewport-type';
import type { ListingsParams } from './listingsDescriptor';

export type PopupState =
  | {|
      type: 'MARKER_POPUP',
      clusterId: number,
      ids: null | $ReadOnlyArray<string>,
      pointsCount: number,
      scrollOffset: number,
      loadedCount: number,
    |}
  | {|
      type: 'PROPERTY_POPUP',
      propertyId: string,
      hoveredClusterIds: null | $ReadOnlyArray<number | null | void>,
    |};

const deepState: DeepState<{|
  popupState: null | PopupState,
  mapCenterOffset: {| +x: number, +y: number |},
  // popupState.propertyId
  // hoveredId: null | string,

  // popupState.clusterId
  // clickedClusterId: null | number,
  // we pass clusterId for every zoom level for every marker
  // hoveredClusterIds: null | $ReadOnlyArray<number | null | void>,
  // to show popup we need to have map of clusterId => divRef
  clusterIdToHtmlElementRef: Map<number, {| current: null | HTMLElement |}>,
  isListScrolling: boolean,
  listingsWindowScroll: {|
    position: number,
    hash: string,
  |},
|}> = createDeepState();

export const {
  // useStore,
  // Provider: ListingsStateProvider,
  useSelector,
  useReduce,
} = deepState;

// This is hacky solution to hold history state at nextjs
// because of some ssr issues userState is not supported in next
// official idea to hold needed state in query and hide that query vars from `as`
// We can't use it because of performance reasons as every query param change caused full app rerender
// We cant use it router onChangeStart event - as it will call onChangeStart again
// We cant use custom window.history.replaceState and pushState as like as here https://github.com/developit/histore/blob/master/histore.js
// as it causes incompatibilities with SSR and browser rendering
// The idea is to use `window.history.state.options` state key as nextjs uses it internally for some edgecases in a same manner
const readHistoryValue = () =>
  typeof window === 'undefined'
    ? null
    : window.history.state?.options?.__state ?? null;

const updateHistoryValue = (newValue, method) => {
  window.history[method](
    {
      ...window.history.state,
      options: {
        ...(window.history.state?.options ?? null),
        __state: newValue,
      },
    },
    undefined,
    undefined,
  );
};

const serializeState = state => ({
  popupState: state.popupState,
  mapCenterOffset: state.mapCenterOffset,
  listingsWindowScroll: state.listingsWindowScroll,
});

const deserializeState = histState => histState;

export const ListingsStateProvider = (props: {|
  children: React.Node,
|}): React.Node => {
  const listingsInitialState = {
    popupState: null,
    clusterIdToHtmlElementRef: new Map(),
    isListScrolling: false,
    mapCenterOffset: { x: 0, y: 0 },
    listingsWindowScroll: {
      position: 0,
      hash: '',
    },
  };

  const viewportType = useViewportType();

  const store = deepState.useStore(() => ({
    ...listingsInitialState,
    ...deserializeState(readHistoryValue()),
  }));

  useRouterEvents({
    onChangeStart: url => {
      const locationUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      // back-forward used
      const isHistoryChange = locationUrl === url;

      if (!isHistoryChange) {
        // save current state on internal routing like push, Link click etc
        const newValue = serializeState(store.get());
        updateHistoryValue(newValue, 'replaceState');
      }
    },

    onBeforeHistoryChange: url => {
      const locationUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      // back-forward used
      const isHistoryChange = locationUrl === url;

      if (isHistoryChange) {
        // Here we can come only after popstate (back, forward) click
        // We restore vars here and not at onChangeStart because
        // want var change occured immediately before route rendered
        // restore history params and merge with store
        store.set(prev => ({
          ...listingsInitialState,
          ...prev,
          ...deserializeState(readHistoryValue()),
        }));
      }
    },
    onChangeComplete: () => {
      // save state immediately after route rendered
      // so forward button will work well
      const newValue = serializeState(store.get());

      updateHistoryValue(newValue, 'replaceState');
    },
  });

  React.useEffect(() => {
    // https://github.com/facebook/react/issues/14369#issuecomment-468270539
    // no setState here just skip history update if unmounted
    let didUnmount = false;

    const handleUpdates = () => {
      if (didUnmount) {
        return;
      }
      if (viewportType === 'unknown') {
        return;
      }

      const newValue = serializeState(store.get());
      const histValue = readHistoryValue();

      if (!isEqual(newValue, histValue)) {
        // We use push on popup visibility change to put it in history
        // we can't put popup params into query because popup cluster id is not
        // preserved during clusters update algorithm
        updateHistoryValue(newValue, 'replaceState');
      }
    };

    // in case of ctx changed or first update was

    handleUpdates();

    const unsubscribe = store.on(handleUpdates);

    return () => {
      didUnmount = true;
      unsubscribe();
    };
  }, [store, viewportType]);

  return (
    <deepState.Provider store={store}>{props.children}</deepState.Provider>
  );
};

export const useActions = (): ({|
  // onListScroll: (isListScrolling: boolean) => void,
  onListingCardHidePopup: () => void,
  onListingCardShowPopup: (
    id: string,
    clusterIds: null | $ReadOnlyArray<number>,
    isSimilar: boolean,
  ) => void,
  onListingsWindowScroll: (position: number, hash: string) => void,
  onMarkerHidePopup: () => void,
  onMarkerShowPopup: (
    clusterId: number,
    ids: null | $ReadOnlyArray<string>,
    pointsCount: number,
    moveY: number,
  ) => void,
  registerMapMarkerHtmlElementSilently: (
    id: number,
    element: {| current: null | HTMLElement |},
  ) => void,
  unregisterMapMarkerHtmlElementSilently: (id: number) => void,
  updateScrollOffset: (scrollOffset: number, loadedCount: number) => void,
|}) => {
  const reduce = useReduce();

  const updateScrollOffset = useDebounce(
    (scrollOffset: number, loadedCount: number) =>
      reduce(r => {
        const { popupState } = r;
        if (popupState != null && popupState.type === 'MARKER_POPUP') {
          return {
            ...r,
            popupState: { ...popupState, scrollOffset, loadedCount },
          };
        }
        return r;
      }),
    (1000 / 60) * 15,
  );

  const onListingsWindowScroll = useDebounce(
    (position: number, hash: string) =>
      reduce(r => {
        if (
          position !== r.listingsWindowScroll.position ||
          hash !== r.listingsWindowScroll.position
        ) {
          return {
            ...r,
            listingsWindowScroll: {
              position,
              hash,
            },
          };
        }
        return r;
      }),
    (1000 / 60) * 15,
  );

  const animRef = React.useRef(null);

  const handleWindowScroll = React.useCallback(
    (position, hash) => {
      onListingsWindowScroll(position, hash);

      cancelAnimationTimeout(animRef.current);

      animRef.current = requestAnimationTimeout(300, () => {
        reduce(prev =>
          prev.isListScrolling === false
            ? prev
            : { ...prev, isListScrolling: false },
        );
      });

      reduce(prev =>
        prev.isListScrolling === true
          ? prev
          : { ...prev, isListScrolling: true },
      );
    },
    [onListingsWindowScroll, reduce],
  );

  return React.useMemo(
    () => ({
      onMarkerShowPopup: (
        clusterId: number,
        ids: null | $ReadOnlyArray<string>,
        pointsCount: number,
        moveY: number,
      ) =>
        reduce(r => ({
          ...r,
          popupState: {
            type: 'MARKER_POPUP',
            clusterId,
            ids,
            pointsCount,
            scrollOffset: 0,
            loadedCount: 0,
          },

          mapCenterOffset:
            moveY > 0
              ? { x: 0, y: moveY + r.mapCenterOffset.y }
              : r.mapCenterOffset,
        })),

      onMarkerHidePopup: () =>
        reduce(prev =>
          prev.popupState == null
            ? prev
            : {
                ...prev,
                popupState: null,
                mapCenterOffset: { x: 0, y: 0 },
              },
        ),

      updateScrollOffset,
      onListingsWindowScroll: handleWindowScroll,

      onListingCardShowPopup: (
        id: string,
        clusterIds: null | $ReadOnlyArray<number>,
        isSimilar: boolean,
      ) =>
        // isSimilar is a fast hack to inverse clusterIds and find hovered similar cluster on map
        // the main issue is that cluster ids can intersect
        reduce(r => ({
          ...r,
          popupState: {
            type: 'PROPERTY_POPUP',
            propertyId: id,
            hoveredClusterIds:
              isSimilar && clusterIds != null
                ? clusterIds.map(v => -v)
                : clusterIds,
          },
        })),

      onListingCardHidePopup: () =>
        reduce(r => ({
          ...r,
          popupState: null,
        })),
      // We need list of such elements to be able to show popups on hover
      // we can't for now without rewriting popup to put them inside markers
      // so we need to have external access from somewhere
      registerMapMarkerHtmlElementSilently: (
        id: number,
        element: {| current: null | HTMLElement |},
      ) =>
        reduce(prev => {
          prev.clusterIdToHtmlElementRef.set(id, element);
          return { ...prev };
        }),
      unregisterMapMarkerHtmlElementSilently: (id: number) =>
        reduce(prev => {
          prev.clusterIdToHtmlElementRef.delete(id);
          return { ...prev };
        }),
    }),
    [reduce, updateScrollOffset, handleWindowScroll],
  );
};

export const STORAGE_ALERT_DIALOG_FILTERS = 'ALERT_DIALOG_FILTERS';
export const STORAGE_ALERT_DIALOG_SKIP = 'ALERT_DIALOG_SKIP';

export const useAlertDialogState = (
  initialOpen: boolean = false,
): ({|
  isOpen: boolean,
  close: () => void,
  open: () => void,
  conditionallyOpen: (params?: ListingsParams) => boolean,
|}) => {
  const [open, setOpen] = React.useState(initialOpen);

  const conditionallyOpen = params => {
    // empty params, request from a standalone page, nothing to save
    if (params == null || params.placeSlugs.length === 0) {
      return false;
    }

    // manually pressed "do not show this"
    if (sessionStorage.getItem(STORAGE_ALERT_DIALOG_SKIP)) {
      return false;
    }

    // either check if the filter term is changed
    const oldValue = sessionStorage.getItem(STORAGE_ALERT_DIALOG_FILTERS);
    const blacklistKeys = ['showMap', 'page'];
    const sensitiveParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => {
        return blacklistKeys.includes(key) ? false : [key, value];
      }),
    );

    const newValue = JSON.stringify(sensitiveParams);
    sessionStorage.setItem(STORAGE_ALERT_DIALOG_FILTERS, newValue);

    // open if the search params are changed
    if (newValue !== oldValue) {
      setOpen(true);
      return true;
    }

    return false;
  };

  return {
    isOpen: open,
    open: () => setOpen(true),
    conditionallyOpen,
    close: () => setOpen(false),
  };
};
